import {
    Badge,
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Flex,
    HStack,
    Icon,
    SimpleGrid,
    VStack,
    forwardRef,
} from '@chakra-ui/react';
import { LuLock } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import {
    Alert,
    AlertDescription,
    AlertIcon,
    Heading,
    Text,
} from '@/components/v4';
import { Card, CardBody, CardHeader } from '@/components/v4/Card';

import {
    breadcrumbItems,
    customerExpectations,
    features,
    reasons,
} from './constants';

const H3 = forwardRef((props, ref) => (
    <Heading
        as="h3"
        fontSize="24px"
        fontWeight="semibold"
        ref={ref}
        {...props}
    />
));

const H4 = forwardRef((props, ref) => (
    <Heading
        as="h4"
        fontSize="14px"
        fontWeight="semibold"
        ref={ref}
        {...props}
    />
));

const FeatureDisabledPage = () => {
    const { darkMode } = useSelector((state) => state.settings);
    const bgColor = darkMode ? '#121212' : '#f5f5f5';
    const cardBg = darkMode ? 'dark.tcap' : null;
    const textColor = darkMode ? 'white' : '#737373';
    const textBadgeColor = darkMode ? 'white' : 'black';
    const borderColor = darkMode ? 'gray.200' : 'gray.600';
    const ctaBgColor = darkMode ? 'rgba(144, 205, 244, 0.16)' : 'blue.100';

    return (
        <Box bg={bgColor} minH="100vh">
            <VStack spacing={6} align="stretch">
                {/* Breadcrumb */}
                <Breadcrumb fontWeight="medium" fontSize="sm">
                    {breadcrumbItems.map((crumb, index) => (
                        <BreadcrumbItem key={index} isCurrentPage={!crumb.href}>
                            {crumb.href ? (
                                <BreadcrumbLink as={Link} to={crumb.href}>
                                    {crumb.label}
                                </BreadcrumbLink>
                            ) : (
                                <Text>{crumb.label}</Text>
                            )}
                        </BreadcrumbItem>
                    ))}
                </Breadcrumb>

                {/* Header Section */}
                <Flex justifyContent="space-between" alignItems="center">
                    <Box>
                        <Heading size="xl" mb={2}>
                            TCAP Registration Service (TRS)
                        </Heading>
                        <Text color={textColor} fontSize="lg">
                            Advanced UCaaS integration platform - Currently
                            unavailable
                        </Text>
                    </Box>
                    <Badge
                        bg={ctaBgColor}
                        variant="subtle"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="sm">
                        <HStack spacing={2}>
                            <Icon as={LuLock} color="#3182ce" boxSize={4} />
                            <Text
                                fontSize="xs"
                                textTransform="capitalize"
                                color={textBadgeColor}>
                                Feature Disabled
                            </Text>
                        </HStack>
                    </Badge>
                </Flex>

                {/* Info Alert */}
                <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                        The TRS (TCAP Registration Service) feature is not
                        currently enabled for your account.
                    </AlertDescription>
                </Alert>

                {/* Content Grid */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {/* Left Column - Company Information */}
                    <Card bg={cardBg} borderColor={borderColor}>
                        <CardBody>
                            <VStack spacing={4} align="stretch">
                                {reasons.map((reason, index) => (
                                    <Box
                                        key={`${reason.title}-${index}`}
                                        borderLeft="4px"
                                        borderColor="brand.500"
                                        pl={4}>
                                        <H4 mb={1}>{reason.title}</H4>
                                        <Text fontSize="sm" color={textColor}>
                                            {reason.description}
                                        </Text>
                                    </Box>
                                ))}
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* Right Column - Customer Expectations */}
                    <Card bg={cardBg} borderColor={borderColor}>
                        <CardBody>
                            <VStack spacing={4} align="stretch">
                                {customerExpectations.map(
                                    (expectation, index) => (
                                        <Box
                                            key={index}
                                            borderLeft="4px"
                                            borderColor="brand.500"
                                            pl={4}>
                                            <H4 mb={1}>{expectation.title}</H4>
                                            <Text
                                                fontSize="sm"
                                                color={textColor}>
                                                {expectation.description}
                                            </Text>
                                        </Box>
                                    ),
                                )}
                            </VStack>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                {/* Features Grid */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                        <Heading size="lg">TRS Features & Benefits</Heading>
                        <Text color={textColor} mt={1}>
                            Advanced capabilities available when TRS is enabled
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <SimpleGrid
                            columns={{ base: 1, md: 2, lg: 3 }}
                            spacing={6}>
                            {features.map((feature, index) => (
                                <HStack
                                    key={index}
                                    spacing={3}
                                    align="flex-start">
                                    <Box
                                        bg="brand.50"
                                        p={2}
                                        borderRadius="lg"
                                        flexShrink={0}>
                                        <Icon
                                            as={feature.icon}
                                            boxSize={6}
                                            color="brand.500"
                                        />
                                    </Box>
                                    <Box>
                                        <H4 mb={1}>{feature.title}</H4>
                                        <Text fontSize="sm" color={textColor}>
                                            {feature.description}
                                        </Text>
                                    </Box>
                                </HStack>
                            ))}
                        </SimpleGrid>
                    </CardBody>
                </Card>

                {/* CTA Section */}
                <Card bg={ctaBgColor} borderColor="blue.100">
                    <CardBody>
                        <VStack spacing={4} textAlign="center">
                            <H3 fontSize="20px">Ready to Enable TRS?</H3>
                            <Text color={textColor} maxW="2xl" mx="auto">
                                Contact our team to discuss how TRS can
                                transform your UCaaS and PBX integration,
                                providing seamless connectivity and advanced
                                features for your organization.
                            </Text>
                        </VStack>
                    </CardBody>
                </Card>
            </VStack>
        </Box>
    );
};

export default FeatureDisabledPage;
